import { useCallback, useEffect, useState } from "react";
import { CompaniesTableProps } from ".";
import { CompanyFilter, createCompanyFilterQueryParams, defaultCompanyFilter } from "../../types/filters/CompanyFilter";
import { getCompanySummariesByFilterAsync } from "../../apiClient";
import { showDangerDialog, showSuccessAlert } from "../../common";
import CompanySummary from "../../types/CompanySummary";
import { PaginatedCompanySummaries } from "../../types/responses/PaginatedCompanySummaries";

function useCompaniesTable(props: CompaniesTableProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [companyIdToUpdate, setCompanyIdToUpdate] = useState<string>();
    const [companyIdToDelete, setCompanyIdToDelete] = useState<string>();
    const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);

    const showUpdateCompanyModal = !!companyIdToUpdate;
    const showDeleteCompanyModal = !!companyIdToDelete;

    const [filter, setFilter] = useState<CompanyFilter>(() => {
        const params = new URLSearchParams(window.location.search);
        const initialFilter = defaultCompanyFilter({
            searchText: params.get("searchText") ?? undefined,
            pageSize: params.get("pageSize") ? parseInt(params.get("pageSize")!) : undefined,
            pageIndex: params.get("pageIndex") ? parseInt(params.get("pageIndex")!) : undefined
        });

        return initialFilter;
    });
    const [paginatedCompanies, setCompanies] = useState<PaginatedCompanySummaries>({ companies: [], totalCount: 0 });

    const totalPages = Math.ceil(paginatedCompanies.totalCount / filter.pageSize);

    const fetchCompaniesAsync = useCallback(async () => {
        const fetchResult = await getCompanySummariesByFilterAsync(filter);
        setIsLoading(false);

        if (fetchResult.failed) {
            console.error("Failed to fetch companies with filter", filter, "Error:", fetchResult.error);
            showDangerDialog(props.resources.get("Error"), props.resources.get("CompaniesFetchError"));
            return;
        }

        setCompanies(fetchResult.value);
    }, [filter, props.resources]);

    useEffect(() => {
        fetchCompaniesAsync();
    }, [fetchCompaniesAsync]);

    useEffect(() => {
        const urlSearchParams = createCompanyFilterQueryParams(filter);
        window.history.replaceState(null, "", `${window.location.pathname}?${urlSearchParams}`);
    }, [filter]);

    async function handleCompanyCreatedAsync() {
        showSuccessAlert(props.resources.get("CompanyCreated"));
        setShowCreateCompanyModal(false);

        await fetchCompaniesAsync();
    }

    async function handleCompanyUpdatedAsync() {
        showSuccessAlert(props.resources.get("CompanyUpdated"));
        setCompanyIdToUpdate(undefined);

        await fetchCompaniesAsync();
    }

    async function handleCompanyDeletedAsync() {
        showSuccessAlert(props.resources.get("CompanyDeleted"));
        setCompanyIdToDelete(undefined);
        await fetchCompaniesAsync();
    }

    function handlePageChange(newPage: number) {
        setFilter(prevFilter => ({
            ...prevFilter,
            pageIndex: newPage
        }));
    }

    function handlePageSizeChange(newPageSize: number) {
        setFilter(prevFilter => ({
            ...prevFilter,
            pageSize: newPageSize,
        }));
    }

    function handleSearchTextChange(newSearchText: string) {
        setFilter(prevFilter => ({
            ...prevFilter,
            searchText: newSearchText,
            pageIndex: 1
        }));
    }

    return {
        filter,
        companyIdToUpdate,
        companyIdToDelete,
        showCreateCompanyModal,
        showUpdateCompanyModal,
        showDeleteCompanyModal,
        isLoading,
        totalPages,
        companies: paginatedCompanies.companies,
        handleCompanyCreatedAsync,
        handleCompanyUpdatedAsync,
        handleCompanyDeletedAsync,
        handlePageChange,
        handlePageSizeChange,
        handleSearchTextChange,
        handleFilterChange: setFilter,
        handleShowCreateCompanyModalChange: setShowCreateCompanyModal,
        handleShowUpdateCompanyModal: setCompanyIdToUpdate,
        handleHideUpdateCompanyModal: () => setCompanyIdToUpdate(undefined),
        handleShowDeleteCompanyModal: setCompanyIdToDelete,
        handleHideDeleteCompanyModal: () => setCompanyIdToDelete(undefined)
    };
}

export default useCompaniesTable;
